/* 内置搜索和重置的form */
import React, { ReactNode, useImperativeHandle, useState } from 'react';
import { Form, Button, FormProps } from 'antd';
import { DownOutlined, ReloadOutlined, SearchOutlined, UpOutlined } from '@ant-design/icons';
import cs from 'classnames';
import styles from './index.module.less';
import { each } from '@lhb/func';

export interface V2SearchFormHandles {
  /**
   * @description 重置
   */
  resetFormFields: (isEmit: boolean) => void;
  /**
   * @description form.getFieldsValue
   */
  getFieldsValue: () => any;
  /**
   * @description form.setFieldsValue
   */
  setFieldsValue: () => any;
}
// 带搜索和重置按钮的form
export interface V2SearchFormProps extends FormProps {
  /**
   * @description 点击搜索或重置按钮回调
   */
  onSearch?: (values: { [propname: string]: any }) => void; // 确定
  /**
   * @description 自定义传入className
   */
  className?: string;
  /**
   * @description label所占的宽度
   */
  labelLength?: 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  /**
   * @description 可额外传入的操作，在重置按钮后面
   */
  extra?: ReactNode;
  /**
   * @description 可传入右侧按钮组
   */
  rightOperate?: ReactNode;
  /**
   * @description refs方法转发，可通过转发refs来调用组件内部方法
   */
  onRef?: any;
  /**
   * @description 查询按钮的文字
   * @default 查询
   */
  onOkText?: string;
  /**
   * @description 是否需要查询和重置按钮
   * @default true
   */
  needSelfOperate?: boolean;
  /**
   * @description 外部传入重置按钮操作
   */
  onCustomerReset?: () => void; // 外部传入重置按钮操作
  /**
   * @description 展开收起回调
   */
  onToggle?: (isOpen: boolean) => void;
  /**
   * @description 是否需要重置按钮
   */
  needResetButton?: boolean;
  /**
   * @description 是否需要搜索按钮
   */
  needSearchButton?: boolean;
  /**
   * @description 是否显示按钮的icon
   */
  showButtonIcon?: boolean;
  /**
   * @description 显示搜索框数量
   * @default 6
   */
  showSearchNum?: number;
  /**
   * @description 是否显示更多筛选按钮
   * @default false
   */
  moreBtn?: boolean;
  /**
   * @description 重置操作默认设置
   */
  resetInitValue?: (formValues: any) => any;
  /**
   * @description 默认收起或展开
   */
  defaultExpend?: boolean;
  children?: any;
}
/**
* @description 便捷文档地址
* @see https://reactpc.lanhanba.net/components/form/search-form
*/
const V2SearchForm: React.FC<V2SearchFormProps> = ({
  onSearch,
  children,
  layout = 'inline',
  className,
  labelAlign = 'left',
  labelLength = 4,
  extra,
  rightOperate,
  onRef,
  onOkText,
  needSelfOperate = true,
  showButtonIcon = true,
  form, // 如果外部传入form则由外部传入form控制
  onFinish, // 如果需要对参数进行处理，则可通过传入onFinish函数来自行处理
  onCustomerReset,
  onToggle,
  defaultExpend = false,
  needResetButton = true,
  needSearchButton = true,
  showSearchNum = 6,
  moreBtn = false,
  resetInitValue,
  ...props
}) => {
  const [searchForm] = Form.useForm();
  const [toggleBtnData, setToggleBtnData] = useState<any>({
    open: defaultExpend
  });

  // 外部组件清空form
  useImperativeHandle(onRef, () => ({
    resetFormFields: (isEmit: boolean) => {
      onReset(isEmit);
    },
    getFieldsValue() {
      return (form || searchForm).getFieldsValue();
    },
    setFieldsValue(value) {
      (form || searchForm).setFieldsValue(value);
    }
  }));

  // 查询
  const onFinishSearch = (values: any) => {
    onSearch && onSearch(values);
  };

  // 重置
  const onReset = (isEmit = true) => {
    const values = (form || searchForm).getFieldsValue();
    let newValues = {};
    if (!resetInitValue) {
      (form || searchForm).resetFields();
    } else {
      each(values, (_: any, key: string) => {
        newValues[key] = undefined;
      });
      newValues = {
        ...newValues,
        ...resetInitValue(values),
      };
      (form || searchForm).setFieldsValue(newValues);
    }
    if (!isEmit) return;
    setTimeout(() => {
      onCustomerReset && onCustomerReset();
      if (!resetInitValue) {
        onSearch && onSearch((form || searchForm).getFieldsValue());
      } else {
        onSearch && onSearch(newValues);
      }
    }, 0);
  };

  // 更多塞选切换
  const toggleBtn = () => {
    const open = !toggleBtnData.open;
    setToggleBtnData({ open });
    setTimeout(() => {
      onToggle?.(open);
    }, 0);
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Form
        {...props}
        className={cs(styles.formSearch, styles[`searchLabel${labelLength}`], className)}
        form={form || searchForm}
        layout={layout}
        labelAlign={labelAlign}
        onFinish={onFinish || onFinishSearch}
      >
        {
          React.Children.map(children, (child, index) => {
            if (!moreBtn || (index < showSearchNum || toggleBtnData.open)) {
              return React.cloneElement(child as React.ReactElement, {
                form: form || searchForm,
                key: index,
              });
            } else {
              return (
                <div style={{ display: 'none' }}>{
                  React.cloneElement(child as React.ReactElement, {
                    form: form || searchForm,
                    key: index,
                  })
                }</div>
              );
            }
          })
        }
        {
          needSelfOperate && (
            <Form.Item noStyle>
              <div>
                {
                  moreBtn && (
                    <Button type='link' onClick={toggleBtn}>{toggleBtnData.open
                      ? <span className='color-primary fs-14'>收起 <UpOutlined className='fs-12'/></span> : <span className='color-primary fs-14'>更多筛选 <DownOutlined className='fs-12'/></span> }
                    </Button>
                  )
                }
                {
                  needSearchButton && (
                    <Button type='primary' icon={showButtonIcon ? <SearchOutlined /> : null} htmlType='submit'>
                      {onOkText || '查询'}
                    </Button>
                  )
                }
                {
                  needResetButton ? (
                    <Button
                      style={needSearchButton ? { margin: '0 10px' } : undefined}
                      icon={showButtonIcon ? <ReloadOutlined /> : ''}
                      onClick={() => onReset()}
                    >重置</Button>
                  ) : <></>
                }
                {extra}
              </div>
            </Form.Item>
          )
        }
      </Form>
      <div>
        {rightOperate}
      </div>
    </div>
  );
};

export default V2SearchForm;
